import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CircularProgress, Box, Button, Typography, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import db from '../../services/db';
import { useAuth } from '../../contexts/AuthContext';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { useNotification } from '../../contexts/NotificationContext';
import QRCode from 'react-qr-code'; // ou import { QRCodeCanvas } from 'qrcode.react';

const calculateVencimento = (dataPedido, dias) => {
  const baseDate = new Date(dataPedido || Date.now());
  baseDate.setDate(baseDate.getDate() + dias);
  return baseDate.toISOString().split('T')[0];
};

const PrintablePedido = ({
  isDuplicar = false,
  isEditMode = true,
  isViewMode = false,
}) => {
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [pedidoData, setPedidoData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Estado para controlar exibição do link/QR code
  const [linkGerado, setLinkGerado] = useState(false);

  // LOGO
  const logoPath = user?.matriz?.logo
    ? require(`../../assets/logos/${user.matriz.logo}`)
    : require(`../../assets/logos/default.png`);

  // Função auxiliar para calcular o peso total dos produtos
  const calcularPesoTotalProdutos = async (itens) => {
    const pesos = await Promise.all(
      itens.map(async (item) => {
        const produto = await db.produtos
          .where('idProduto')
          .equals(Number(item.idProduto))
          .first();
        const peso = produto && produto.pesoBruto ? parseFloat(produto.pesoBruto) : 0;
        const quantidade = parseFloat(item.quantidade || 0);
        return peso * quantidade;
      })
    );
    return pesos.reduce((acc, curr) => acc + curr, 0);
  };

  // Busca e formata os dados do pedido
  useEffect(() => {
    const fetchPedido = async () => {
      try {
        if (!id) return;

        const storedUser = await db.currentUser.get('currentUser');
        const vendedor = await db.vendedorModel.get(storedUser.vendedorModelId);

        const pedido = await db.pedidos.get(Number(id));
        if (!pedido) {
          alert('Pedido não encontrado!');
          navigate('/pedidos');
          return;
        }

        const cliente = pedido.idCliente
          ? await db.clientes.get(Number(pedido.idCliente))
          : null;

        const condicaoPagamento = pedido.idCondicaoPagamento
          ? await db.condicaoPagamento
              .where('idCondicaoPagamento')
              .equals(pedido.idCondicaoPagamento)
              .first()
          : null;

        const naturezaOperacao = pedido.idNaturezaOperacao
          ? await db.tiposOperacao
              .where('idNaturezaOperacao')
              .equals(pedido.idNaturezaOperacao)
              .first()
          : null;

        const formaPagto = pedido.idFormaPagamento
          ? await db.formasPagamentos.get(Number(pedido.idFormaPagamento))
          : null;

        const itensFormatados = await Promise.all(
          (pedido.itens || []).map(async (item) => {
            const produto = await db.produtos
              .where('idProduto')
              .equals(Number(item.idProduto))
              .first();
            const peso = produto && produto.pesoBruto ? parseFloat(produto.pesoBruto) : 0;
            return {
              idProduto: item.idProduto,
              descricao: item.nomeProduto,
              codigoProduto: item.codigoProduto || null,
              quantidade: item.quantidade || 0,
              preco: item.valorUnitario || 0,
              total:
                item.valorTotal || item.quantidade * (item.valorUnitario || 0),
              peso,
            };
          })
        );

        const valorTotalProdutos = itensFormatados.reduce(
          (acc, item) => acc + parseFloat(item.total || 0),
          0
        );

        const pesoTotalProdutos = await calcularPesoTotalProdutos(pedido.itens);
        const descontoTotal = pedido.valorDesconto
          ? parseFloat(pedido.valorDesconto)
          : 0;
        const valorFrete = pedido.valorFrete ? parseFloat(pedido.valorFrete) : 0;
        const valorTotalPedido = valorTotalProdutos + valorFrete - descontoTotal;

        let novasParcelas = [];
        if (isDuplicar && condicaoPagamento) {
          const dias = condicaoPagamento.dias
            ? condicaoPagamento.dias
                .split(',')
                .map((dia) => parseInt(dia.trim(), 10))
            : [];
          if (dias.length > 0) {
            const parcelaValor = (valorTotalProdutos / dias.length).toFixed(2);
            novasParcelas = dias.map((dia, index) => ({
              numero: index + 1,
              dataVencimento: calculateVencimento(pedido.dataPedido, dia),
              valor: parcelaValor,
            }));
          }
        }

        const newPedidoData = {
          idPedido: isDuplicar ? 0 : pedido.idPedido,
          idLocal: isDuplicar ? 0 : pedido.idPedido,
          sincronismo: pedido.sincronismo || false,
          uuidMobile: pedido.uuidMobile, // para gerar o link e QR code
          dadosGerais: {
            cliente: cliente || null,
            naturezaOperacao: naturezaOperacao || null,
            formaPagamento: formaPagto || null,
            condicaoPagamento: condicaoPagamento || null,
            ramoAtividade: cliente?.ramoAtividade || null,
            vendedor: vendedor?.nome || '',
          },
          itens: itensFormatados,
          frete: { valorFrete, transportadora: pedido.transportadora || null },
          parcelas: isDuplicar ? novasParcelas : pedido.parcelas || [],
          valorTotalProdutos,
          descontosTotais: descontoTotal,
          valorTotalPedido,
          pesoTotalProdutos,
          dataPedido: pedido.dataPedido,
          numeroPedido: pedido.numeroPedido,
          dataEntrega: pedido.dataEntrega,
        };

        setPedidoData(newPedidoData);
      } catch (error) {
        console.error('Erro ao buscar o pedido:', error);
        alert('Erro ao carregar o pedido.');
        navigate('/pedidos');
      } finally {
        setLoading(false);
      }
    };

    if (isEditMode || isViewMode) {
      fetchPedido();
    }
  }, [id, isEditMode, isViewMode, navigate, isDuplicar]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!pedidoData) {
    return <div>Pedido não encontrado.</div>;
  }

  // Função para gerar o PDF e salvar no Android (ou dispositivo móvel) via Capacitor
  const handleImprimirPedido = async () => {
    const input = document.querySelector('.printable-container');
    if (!input) {
      alert('Elemento para PDF não encontrado!');
      return;
    }

    try {
      // Solicita permissão de armazenamento (Android)
      const permissionStatus = await Filesystem.requestPermissions();
      console.log('Status da permissão:', permissionStatus);
      if (
        permissionStatus.publicStorage !== 'granted' &&
        permissionStatus.publicStorage !== undefined
      ) {
        showNotification('Permissão de armazenamento não concedida.');
        return;
      }
    } catch (permError) {
      console.error('Erro ao solicitar permissões:', permError);
      showNotification('Erro ao solicitar permissões para arquivos.');
      return;
    }

    // Gera o PDF via html2canvas + jsPDF
    await new Promise((resolve) => setTimeout(resolve, 500)); // pequeno delay
    const options = { scale: 2, useCORS: true, allowTaint: false };

    try {
      const canvas = await html2canvas(input, options);
      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgProps = pdf.getImageProperties(imgData);
      const imgWidth = pdfWidth;
      const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      while (position + imgHeight > pdfHeight) {
        position -= pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      }

      // Converte PDF para base64 (sem prefixo datauri)
      const pdfBase64 = pdf.output('datauristring').split(',')[1];
      const fileName = `pedido-${pedidoData.idPedido || 'novo'}.pdf`;

      // Salva o PDF no diretório Documents
      await Filesystem.writeFile({
        path: fileName,
        data: pdfBase64,
        directory: Directory.Documents,
        recursive: true,
      });

      showNotification(`PDF gerado e salvo com sucesso em ${fileName}`);
    } catch (error) {
      console.error('Erro ao gerar/salvar PDF:', error);
      showNotification('Houve um erro ao gerar o PDF. Tente novamente.');
    }
  };

  // Função para gerar link e exibir QR code
  const handleGerarLink = () => {
    if (!pedidoData.uuidMobile) {
      showNotification('Não há UUID disponível para gerar link.');
      return;
    }
    setLinkGerado(true);
  };

  const handleBack = () => navigate('/pedidos');

  // Estilos
  const stylesObj = {
    container: {
      padding: '20px',
      fontFamily: 'Arial, sans-serif',
      maxWidth: '900px',
      margin: '0 auto',
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: '4px',
    },
    section: { marginBottom: '20px' },
    header: {
      borderBottom: `2px solid ${theme.palette.divider}`,
      marginBottom: '10px',
      paddingBottom: '5px',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '10px',
    },
    th: {
      border: `1px solid ${theme.palette.divider}`,
      padding: '5px',
      backgroundColor: theme.palette.primary.main,
      fontSize: '0.9em',
      color: theme.palette.primary.contrastText,
    },
    td: {
      border: `1px solid ${theme.palette.divider}`,
      padding: '5px',
      fontSize: '0.9em',
      textAlign: 'center',
    },
    strong: { fontWeight: 'bold' },
    buttonContainer: {
      textAlign: 'center',
      marginBottom: '20px',
      display: 'flex',
      gap: '10px',
      justifyContent: 'center',
      flexWrap: 'wrap',
    },
    printButton: {
      padding: '10px 20px',
      fontSize: '16px',
      cursor: 'pointer',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      border: 'none',
      borderRadius: '4px',
    },
    qrCodeContainer: {
      marginTop: '20px',
      textAlign: 'center',
      border: `1px dashed ${theme.palette.divider}`,
      padding: '10px',
      borderRadius: '4px',
    },
    title: {
      textAlign: 'center',
      marginBottom: '20px',
    },
  };

  return (
    <div style={stylesObj.container} className="printable-container">
      <style>
        {`
          @media print {
            @page { margin: 20mm; }
            .printable-container { color: #000 !important; background-color: #fff !important; }
            .no-print { display: none; }
          }
          @media screen and (max-width: 600px) {
            .printable-container { padding: 10px; font-size: 0.9em; }
            table, thead, tbody, th, td, tr { font-size: 0.8em; }
          }
        `}
      </style>

      <div className="no-print" style={stylesObj.title}>
        {logoPath && (
          <img
            src={logoPath}
            alt="Logo da Empresa"
            style={{ maxHeight: '80px', marginBottom: '10px' }}
          />
        )}
        <Typography variant="h5">Pedido de Venda</Typography>
      </div>

      <Box style={stylesObj.buttonContainer} className="no-print">
        <Button
          variant="contained"
          style={stylesObj.printButton}
          onClick={handleGerarLink}
        >
          Gerar Link
        </Button>



        <Button variant="contained" style={stylesObj.printButton} onClick={handleBack}>
          Voltar
        </Button>
      </Box>

      {/* Exibe o QR code e o link, caso tenha sido gerado */}
      {linkGerado && pedidoData.uuidMobile && (
        <Box style={stylesObj.qrCodeContainer}>
          <Typography variant="h6" gutterBottom>
            Link Gerado
          </Typography>
          <Typography variant="body1" paragraph>
          
          </Typography>
            <TextField
                          label="LINK GERADO"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={"https://app.integracenter.com.br/pedido/"+pedidoData.uuidMobile}
        
                        />
          <QRCode
            value={`https://app.integracenter.com.br/pedido/${pedidoData.uuidMobile}`}
            size={300}
          />
        </Box>
      )}

      {/* Versão que aparece somente na impressão */}
      <div style={{ textAlign: 'center', marginBottom: '20px' }} className="print-only">
        <img src={logoPath} alt="Logo da Empresa" style={{ maxHeight: '80px' }} />
        <h2>Pedido de Venda</h2>
      </div>

      {/* Dados do Cliente */}
      <section style={stylesObj.section}>
        <h2 style={stylesObj.header}>Dados do Cliente</h2>
        <p>
          <span style={stylesObj.strong}>Cliente:</span>{' '}
          {pedidoData.dadosGerais?.cliente?.nome || '-'}
        </p>
        <p>
          <span style={stylesObj.strong}>Endereço:</span>{' '}
          {pedidoData.dadosGerais?.cliente?.endereco || '-'}
        </p>
        <p>
          <span style={stylesObj.strong}>Cidade/UF:</span>{' '}
          {pedidoData.dadosGerais?.cliente?.cidade || '-'} /{' '}
          {pedidoData.dadosGerais?.cliente?.estado || '-'}
        </p>
        <p>
          <span style={stylesObj.strong}>Telefone:</span>{' '}
          {pedidoData.dadosGerais?.cliente?.telefone || '-'}
        </p>
        <p>
          <span style={stylesObj.strong}>CNPJ/CPF:</span>{' '}
          {pedidoData.dadosGerais?.cliente?.documento || '-'}
        </p>
        <p>
          <span style={stylesObj.strong}>CEP:</span>{' '}
          {pedidoData.dadosGerais?.cliente?.cep || '-'}
        </p>
        <p>
          <span style={stylesObj.strong}>Inscrição Estadual:</span>{' '}
          {pedidoData.dadosGerais?.cliente?.inscricaoEstadual || '-'}
        </p>
      </section>

      {/* Informações sobre o Pedido */}
      <section style={stylesObj.section}>
        <h2 style={stylesObj.header}>Informações sobre o Pedido</h2>
        <p>
          <span style={stylesObj.strong}>Emissão:</span>{' '}
          {pedidoData.dataPedido
            ? new Date(pedidoData.dataPedido).toLocaleString()
            : '-'}
        </p>
        <p>
          <span style={stylesObj.strong}>Número do Pedido:</span>{' '}
          {pedidoData.numeroPedido || '-'}
        </p>
        <p>
          <span style={stylesObj.strong}>Natureza da Operação:</span>{' '}
          {pedidoData.dadosGerais?.naturezaOperacao?.descricaoCompleta || '-'}
        </p>
        <p>
          <span style={stylesObj.strong}>Data de Entrega:</span>{' '}
          {pedidoData.dataEntrega
            ? new Date(pedidoData.dataEntrega).toLocaleDateString()
            : '-'}
        </p>
      </section>

      {/* Informações sobre Pagamento */}
      <section style={stylesObj.section}>
        <h2 style={stylesObj.header}>Informações sobre Pagamento</h2>
        <p>
          <span style={stylesObj.strong}>Cond. Pagamento:</span>{' '}
          {pedidoData.dadosGerais?.condicaoPagamento?.descricao || '-'}
        </p>
        <p>
          <span style={stylesObj.strong}>Forma Pagamento:</span>{' '}
          {pedidoData.dadosGerais?.formaPagamento?.descricao || '-'}
        </p>
        <p>
          <span style={stylesObj.strong}>Vendedor:</span>{' '}
          {pedidoData.dadosGerais?.vendedor || '-'}
        </p>
      </section>

      {/* Itens do Pedido */}
      <section style={stylesObj.section}>
        <h2 style={stylesObj.header}>Itens do Pedido</h2>
        <table style={stylesObj.table}>
          <thead>
            <tr>
              <th style={stylesObj.th}>Código</th>
              <th style={stylesObj.th}>Descrição</th>
              <th style={stylesObj.th}>Quantidade</th>
              <th style={stylesObj.th}>Valor Unitário</th>
              <th style={stylesObj.th}>Valor Total</th>
              <th style={stylesObj.th}>Peso Unitário</th>
              <th style={stylesObj.th}>Peso Total</th>
            </tr>
          </thead>
          <tbody>
            {pedidoData.itens &&
              pedidoData.itens.map((item, index) => (
                <tr key={index}>
                  <td style={stylesObj.td}>{item.codigoProduto || '-'}</td>
                  <td style={stylesObj.td}>{item.descricao || '-'}</td>
                  <td style={stylesObj.td}>{item.quantidade || '-'}</td>
                  <td style={stylesObj.td}>
                    R$ {parseFloat(item.preco).toFixed(2)}
                  </td>
                  <td style={stylesObj.td}>
                    R$ {parseFloat(item.total).toFixed(2)}
                  </td>
                  <td style={stylesObj.td}>
                    {item.peso ? parseFloat(item.peso).toFixed(2) : '-'} kg
                  </td>
                  <td style={stylesObj.td}>
                    {item.peso
                      ? (item.peso * item.quantidade).toFixed(2)
                      : '-'}{' '}
                    kg
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </section>

      {/* Totais e Outras Informações */}
      <section style={stylesObj.section}>
        <h2 style={stylesObj.header}>Totais e Outras Informações</h2>
        <p>
          <span style={stylesObj.strong}>Valor Total dos Produtos:</span>{' '}
          {pedidoData.valorTotalProdutos
            ? pedidoData.valorTotalProdutos.toFixed(2)
            : '-'}
        </p>
        <p>
          <span style={stylesObj.strong}>Descontos Totais:</span>{' '}
          {pedidoData.descontosTotais
            ? pedidoData.descontosTotais.toFixed(2)
            : '-'}
        </p>
        <p>
          <span style={stylesObj.strong}>Valor Total do Pedido:</span>{' '}
          {pedidoData.valorTotalPedido
            ? pedidoData.valorTotalPedido.toFixed(2)
            : '-'}
        </p>
        <p>
          <span style={stylesObj.strong}>Peso Total dos Produtos:</span>{' '}
          {pedidoData.pesoTotalProdutos
            ? pedidoData.pesoTotalProdutos.toFixed(2)
            : '-'}{' '}
          kg
        </p>
      </section>
    </div>
  );
};

export default PrintablePedido;
